<template lang="pug">
.similar-documents
  managementPage.scrollable(:title="$t(`${baseTranslationPath}.title`)"
                  @fetchData="loadList"
                  :searchPageSize="searchPageSize",
                  :showTotals="true"
                  :teamFilter="false"
                  :creatable="false"
                  :pagination="pagination"
                  ref="managementPage")
      template(v-slot:body)
        aiq-table.manage-documents-scrollbar(:data="items" :default-sort="defaultSortOrder" v-loading="loading")
          aiq-table-column(:label="$t(`${baseTranslationPath}.document`)"
                          label-class-name="is-bordered"
                          min-width="60"
                          :sortable="true"
                          :sort-method="onSortField('document.name')")
                template(v-slot:default="scope")
                  .title(@click="openDocument(scope.row.document)")
                    font-awesome-icon(:icon="getIcon(scope.row.document)[0]" :class="getIcon(scope.row.document)[1]")
                    span {{ getDocName(scope.row, 'document') }}
          aiq-table-column(:label="$t(`${baseTranslationPath}.duplicate`)"
                          label-class-name="is-bordered"
                          min-width="60"
                          :sortable="true"
                          :sort-method="onSortField('duplicate.name')")
                template(v-slot:default="scope")
                  .title(@click="openDocument(scope.row.duplicate)")
                    font-awesome-icon(:icon="getIcon(scope.row.duplicate)[0]" :class="getIcon(scope.row.duplicate)[1]")
                    span {{ getDocName(scope.row, 'duplicate') }}
          aiq-table-column(prop="semantic_score"
                          :label="$t(`${baseTranslationPath}.semantic_score`)"
                          label-class-name="is-bordered"
                          min-width="35"
                          :sortable="true"
                          :sort-method="onSortScore('semantic_score')")
                template(v-slot:default="scope")
                   span {{ getScore(scope.row, 'semantic_score') }}
          aiq-table-column(prop="syntactic_score"
                          :label="$t(`${baseTranslationPath}.syntactic_score`)"
                          label-class-name="is-bordered"
                          min-width="35"
                          :sortable="true"
                          :sort-method="onSortScore('syntactic_score')")
                template(v-slot:default="scope")
                   span {{ getScore(scope.row, 'syntactic_score') }}
          aiq-table-column(prop="created_at"
                          :label="$t(`${baseTranslationPath}.created_at`)"
                          label-class-name="is-bordered"
                          min-width="45"
                          :sortable="true"
                          :sort-method="onSortTime('created_at')")
                template(v-slot:default="scope")
                   span {{ getItemTime(scope.row, 'created_at') }}
          aiq-table-column(align="center"
                          label-class-name="is-bordered"
                          width="71")
            template(v-slot:default="scope")
              aiq-button(link type="danger" size="small" @click="deleteDuplicate(scope.row)") {{ $t(`global.delete`) }}
</template>
<script>
import get from 'lodash/get';
import { mapState } from 'vuex';
import * as log from 'loglevel';
import GetGlobalSource from '@/libs/mixins/GetGlobalSource';
import ManagementPage from '@/components/ManagementPage/ManagementPage.vue';
import { PAGINATION_DEFAULT } from '@/constants/pagination';
import dayjs from '@/localPlugins/dayjs';
import { iconClasses, RECORD_KINDS } from '../SmartDrive/smartDriveUtil';

const SEARCH_PAGE_SIZE = 25;
const SCORE_PRECISION = 2;

export default {
  name: 'SimilarDocuments',
  mixins: [GetGlobalSource],
  components: {
    ManagementPage,
  },
  data() {
    return {
      loading: true,
      pagination: PAGINATION_DEFAULT,
      searchPageSize: SEARCH_PAGE_SIZE,
      defaultSortOrder: { prop: 'created_at', order: 'descending' },
      baseTranslationPath: 'settings_tab.smart_drive_tab.similar_documents',
    };
  },
  computed: {
    ...mapState({
      items: state => state.documents.duplicates,
    }),
  },
  methods: {
    async loadList({ searchTerm, searchOffset }) {
      const params = {
        limit: this.searchPageSize,
        query: searchTerm,
        offset: searchOffset,
      };

      const response = await this.$store.dispatch(
        'documents/getDocumentDuplicates',
        [params, searchOffset === 0],
      );
      this.loading = false;
      this.pagination = get(response, 'data.pagination', PAGINATION_DEFAULT);
      return response;
    },
    getDocName(item, field) {
      return get(item, `${field}.name`, '');
    },
    getScore(item, field) {
      const score = get(item, field);
      if (score === null || score === undefined) return 'N/A';
      return Number(score).toFixed(SCORE_PRECISION);
    },
    getItemTime(item, field) {
      const time = get(item, field, '');
      return dayjs(time).format('MMM D, YYYY h:mm A');
    },
    getIcon(doc) {
      return iconClasses(get(doc, 'meta.object_type', ''));
    },
    /**
     * Resolves the smart drive editor query type for the given document,
     * mirroring the mapping used by the Smart Drive list.
     * @param {Object} doc
     * @returns {string}
     */
    getEditorType(doc) {
      const kind = get(doc, 'meta.object_type', '');
      switch (kind) {
        case RECORD_KINDS.FOLDER:
          return 'folder';
        case RECORD_KINDS.URL:
        case RECORD_KINDS.HTML:
        case '':
          return 'url';
        case RECORD_KINDS.SFTP:
          return 'sftp';
        case RECORD_KINDS.SHAREPOINT:
          return 'sharepoint';
        case RECORD_KINDS.URLLIST:
          return 'urllist';
        case RECORD_KINDS.HUBSPOT:
          return 'hubspot';
        default:
          return RECORD_KINDS.FILE;
      }
    },
    /**
     * Opens the smart drive editor of the given document in a new browser tab.
     * @param {Object} doc
     */
    async openDocument(doc) {
      // Should fetch document again to get a file url
      try {
        const response = await this.$store.dispatch('documents/getDocument', [get(doc, 'id'), []]);
        const document = get(response, 'data', {});
        const url = await this.getFileUrl(get(document, 'file.payload'));
        if (url) {
          window.open(url, '_blank');
        }
      } catch (error) {
        log.error('Failed to open document', error);
        this.$aiq.notify.error(this.$t(`${this.baseTranslationPath}.open_error`));
      }
    },
    async deleteConfirm() {
      try {
        await this.$aiq.confirm(
          this.$t(`${this.baseTranslationPath}.delete_confirm.title`),
          this.$t(`${this.baseTranslationPath}.delete_confirm.message`),
        );
        return true;
      } catch {
        return false;
      }
    },
    async deleteDuplicate(item) {
      if (!(await this.deleteConfirm())) {
        return;
      }

      try {
        await this.$store.dispatch('documents/deleteDocumentDuplicate', {
          documentId: get(item, 'document_id'),
          itemId: get(item, 'id'),
        });
        this.$aiq.notify.success(this.$t(`${this.baseTranslationPath}.delete_success`));
        this.$refs.managementPage.resetAndFetchData();
      } catch (error) {
        const fallback = this.$t(`${this.baseTranslationPath}.delete_error`);
        this.$aiq.notify.error(get(error, 'response.data.message', fallback));
      }
    },
    onSortField(column) {
      return (a, b) => {
        const aValue = (get(a, column) || '').toLowerCase().replace(/\s+/g, '');
        const bValue = (get(b, column) || '').toLowerCase().replace(/\s+/g, '');
        return aValue < bValue ? 1 : -1;
      };
    },
    onSortScore(field) {
      return (a, b) => {
        const aVal = Number(get(a, field, 0));
        const bVal = Number(get(b, field, 0));
        return aVal <= bVal ? 1 : -1;
      };
    },
    onSortTime(field) {
      return (a, b) => {
        const aVal = get(a, field, '');
        const bVal = get(b, field, '');
        return aVal <= bVal ? 1 : -1;
      };
    },
  },
};
</script>
<style lang="scss" scoped>
@import "../SmartDrive/icon.scss";

.similar-documents {
  height: calc(100vh - 150px);
  overflow-y: auto;
}
.title {
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;

  &:hover span {
    text-decoration: underline;
  }
}
:deep(tr) {
  height: 50px !important;
}
:deep(.content-inner) {
  width: 99% !important;
}
</style>
